
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Tag,
  List,
  Space,
  Typography,
  Modal,
  Form,
  Badge,
  Tabs,
  Switch,
  Tooltip,
  Popconfirm,
  Statistic,
  Avatar,
  Rate,
  message
} from 'antd';
import {
  BellOutlined,
  SendOutlined,
  EyeOutlined,
  DeleteOutlined,
  MailOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  ToolOutlined,
  UserOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  StarOutlined,
  DollarOutlined,
  PlusOutlined,
  FilterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import notificationService, { Notification, NotificationType, NotificationPriority } from '../services/notificationService';
import { techniciansService, Technician } from '../services/techniciansService';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface SendingStats {
  total_sent: number;
  today_sent: number;
  total_recipients: number;
  delivery_rate: number;
}

interface NotificationTemplate {
  id: number;
  name: string;
  type: string;
  subject: string;
  content: string;
}

interface CreateNotificationData {
  type: string;
  priority: string;
  title: string;
  message: string;
  recipients?: number[];
  category?: string;
  scheduled_at?: string;
}

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';
  
  // États pour les onglets principaux
  const [activeTab, setActiveTab] = useState('notifications');

  // États pour les notifications
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [filters, setFilters] = useState({
    is_read: undefined as boolean | undefined,
    type: undefined as NotificationType | undefined,
    priority: undefined as NotificationPriority | undefined
  });

  // États pour l'envoi de notifications
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [sendingStats, setSendingStats] = useState<SendingStats>({
    total_sent: 0,
    today_sent: 0,
    total_recipients: 0,
    delivery_rate: 0
  });

  // États pour les modaux
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  // États pour les formulaires
  const [sendForm] = Form.useForm();
  const [templateForm] = Form.useForm();

  // États pour les évaluations
  const [ratings, setRatings] = useState<any[]>([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [selectedTechnician, setSelectedTechnician] = useState<string>('all');

  // Charger les notifications
  const loadNotifications = useCallback(async (page = 1, pageSize = 20) => {
    try {
      setLoading(true);
      const response = await notificationService.getMyNotifications({
        ...filters,
        offset: (page - 1) * pageSize,
        limit: pageSize,
      });

      setNotifications(response.data);
      setPagination({
        current: page,
        pageSize,
        total: response.pagination?.total || response.data.length
      });
      
      // Compter les non lues
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error: any) {
      console.error('Erreur lors du chargement des notifications:', error);
      message.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Charger les modèles et destinataires
  const loadTemplatesAndRecipients = useCallback(async () => {
    try {
      // Simuler le chargement des templates (pas d'API disponible)
      setTemplates([]);
    } catch (error: any) {
      console.error('Erreur lors du chargement des données:', error);
    }
  }, []);

  // Charger les statistiques d'envoi
  const loadSendingStats = useCallback(async () => {
    try {
      // API non disponible pour le moment
      setSendingStats({
        total_sent: 0,
        today_sent: 0,
        total_recipients: 0,
        delivery_rate: 0
      });
    } catch (error: any) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  }, []);

  // Charger les évaluations et techniciens
  const loadRatings = useCallback(async () => {
    try {
      setRatingsLoading(true);
      const apiUrl = API_URL;

      const [ratingsRes, techRes] = await Promise.allSettled([
        fetch(`${apiUrl}/interventions?status=completed&has_rating=true&limit=500`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }).then(res => {
          if (!res.ok) throw new Error('Erreur réseau');
          return res.json();
        }),
        techniciansService.getTechnicians({ limit: 1000 })
      ]);

      if (ratingsRes.status === 'fulfilled' && ratingsRes.value?.success) {
        const interventions = ratingsRes.value.data?.interventions || [];
        const formattedRatings = interventions.map((i: any) => ({
          intervention_id: i.id,
          intervention_title: i.title,
          rating: i.rating,
          review: i.review,
          customer_name: `${i.customer?.first_name || ''} ${i.customer?.last_name || ''}`.trim() || 'Client',
          technician_id: i.technician_id || i.technician?.id || i.technician?.user_id,
          technician_name: `${i.technician?.first_name || ''} ${i.technician?.last_name || ''}`.trim() || 'Non assigné',
          rated_at: i.updated_at
        }));
        setRatings(formattedRatings);
      }

      if (techRes.status === 'fulfilled') {
        setTechnicians(techRes.value.data || []);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des évaluations:', error);
      message.error('Erreur lors du chargement des évaluations');
    } finally {
      setRatingsLoading(false);
    }
  }, []);

  // Liste combinée et unique des techniciens disponibles
  const allTechniciansOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();

    technicians.forEach((t) => {
      const name = `${t.first_name || ''} ${t.last_name || ''}`.trim();
      if (name) {
        const key = String(t.id || t.user_id);
        map.set(key, { id: key, name });
      }
    });

    ratings.forEach((r) => {
      if (r.technician_name && r.technician_name !== 'Non assigné') {
        const key = r.technician_id ? String(r.technician_id) : r.technician_name;
        if (!map.has(key)) {
          map.set(key, { id: key, name: r.technician_name });
        }
      }
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [technicians, ratings]);

  // Évaluations filtrées selon le technicien sélectionné
  const filteredRatings = useMemo(() => {
    if (selectedTechnician === 'all') return ratings;
    return ratings.filter((r) => {
      const key = r.technician_id ? String(r.technician_id) : r.technician_name;
      return key === selectedTechnician || r.technician_name === selectedTechnician;
    });
  }, [ratings, selectedTechnician]);

  // Statistiques filtrées selon le technicien sélectionné
  const filteredRatingsStats = useMemo(() => {
    const total = filteredRatings.length;
    const sum = filteredRatings.reduce((acc, r) => acc + (r.rating || 0), 0);
    const average = total > 0 ? sum / total : 0;
    const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    filteredRatings.forEach((r) => {
      if (r.rating >= 1 && r.rating <= 5) {
        breakdown[r.rating as keyof typeof breakdown]++;
      }
    });
    return { total, average, breakdown };
  }, [filteredRatings]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (activeTab === 'send') {
      loadTemplatesAndRecipients();
      loadSendingStats();
    } else if (activeTab === 'ratings') {
      loadRatings();
    }
  }, [activeTab, loadTemplatesAndRecipients, loadSendingStats, loadRatings]);

  // Marquer comme lu/non lu
  const toggleReadStatus = async (id: number, isRead: boolean) => {
    try {
      if (!isRead) {
        await notificationService.markAsRead(id);
        message.success('Marqué comme lu');
      } else {
        await notificationService.markAsUnread(id);
        message.success('Marqué comme non lu');
      }
      await loadNotifications(pagination.current, pagination.pageSize);
    } catch (error: any) {
      message.error('Erreur lors de la mise à jour');
    }
  };

  // Supprimer une notification
  const deleteNotification = async (id: number) => {
    try {
      await notificationService.deleteNotification(id);
      await loadNotifications(pagination.current, pagination.pageSize);
      message.success('Notification supprimée');
    } catch (error: any) {
      message.error('Erreur lors de la suppression');
    }
  };

  // Marquer toutes comme lues
  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      await loadNotifications(pagination.current, pagination.pageSize);
      message.success('Toutes les notifications ont été marquées comme lues');
    } catch (error: any) {
      message.error('Erreur lors de la mise à jour');
    }
  };

  // Envoyer une notification
  const sendNotification = async (values: any) => {
    try {
      setLoading(true);
      message.info('Fonctionnalité d\'envoi de notification en développement');
      setSendModalVisible(false);
      sendForm.resetFields();
    } catch (error: any) {
      message.error('Erreur lors de l\'envoi de la notification');
    } finally {
      setLoading(false);
    }
  };

  // Créer un modèle
  const createTemplate = async (values: any) => {
    try {
      // Simuler la création de modèle (API non disponible)
      message.success('Modèle créé avec succès (simulation)');
      setTemplateModalVisible(false);
      templateForm.resetFields();
      await loadTemplatesAndRecipients();
    } catch (error: any) {
      message.error('Erreur lors de la création du modèle');
    }
  };

  // Icônes pour les types de notifications
  const getTypeIcon = (type: string) => {
    // Interventions - icônes spécifiques
    if (type === 'intervention_updated') return <ToolOutlined style={{ color: '#fa8c16' }} />; // Intervention modifiée (orange)
    if (type.includes('intervention')) return <ToolOutlined style={{ color: '#1890ff' }} />; // Autres interventions (bleu)
    // Réclamations
    if (type.includes('complaint')) return <ExclamationCircleOutlined style={{ color: '#f5222d' }} />;
    
    // Commandes
    if (type.includes('order')) return <ShoppingCartOutlined style={{ color: '#52c41a' }} />;
    
    // Devis - icônes spécifiques
    if (type === 'quote_created') return <FileTextOutlined style={{ color: '#13c2c2' }} />; // Nouveau devis
    if (type === 'quote_updated') return <FileTextOutlined style={{ color: '#fa8c16' }} />; // Devis modifié (orange)
    if (type === 'quote_accepted') return <CheckCircleOutlined style={{ color: '#52c41a' }} />; // Devis accepté (vert)
    if (type === 'quote_rejected') return <CloseCircleOutlined style={{ color: '#f5222d' }} />; // Devis rejeté (rouge)
    if (type.includes('quote')) return <FileTextOutlined style={{ color: '#13c2c2' }} />; // Autre devis
    
    // Paiements
    if (type.includes('payment')) return <DollarOutlined style={{ color: '#52c41a' }} />;
    
    // Contrats
    if (type === 'contract_expiring') return <ClockCircleOutlined style={{ color: '#fa8c16' }} />; // Contrat expirant
    if (type.includes('contract')) return <SettingOutlined style={{ color: '#1890ff' }} />;
    
    // Abonnements
    if (type.includes('subscription')) return <UserOutlined style={{ color: '#722ed1' }} />;
    
    // Général
    if (type === 'general') return <BellOutlined style={{ color: '#666' }} />;
    
    return <BellOutlined style={{ color: '#666' }} />;
  };

  // Couleurs pour les priorités
  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'urgent': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'blue';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  // Gérer le clic sur une notification
  const handleNotificationClick = async (notification: Notification) => {
    console.log('🔔 Clic sur notification:', notification);
    
    // Marquer comme lue si pas déjà lu
    if (!notification.is_read) {
      await toggleReadStatus(notification.id, false);
    }

    // Naviguer vers l'actionUrl si disponible (en acceptant les deux formats)
    const url = notification.actionUrl || notification.action_url;
    console.log('🔗 URL de navigation:', url);
    
    if (url) {
      navigate(url);
      console.log('✅ Navigation vers:', url);
    } else {
      console.log('⚠️ Aucune URL d\'action trouvée');
    }
  };

  // Actions pour une notification
  const getNotificationActions = (notification: Notification) => [
    <Tooltip title="Voir les détails" key="view">
      <Button 
        type="text" 
        icon={<EyeOutlined />} 
        onClick={() => {
          setSelectedNotification(notification);
          setViewModalVisible(true);
        }}
      />
    </Tooltip>,
    <Tooltip title={notification.is_read ? "Marquer comme non lu" : "Marquer comme lu"} key="read">
      <Button 
        type="text" 
        icon={<MailOutlined />} 
        onClick={() => toggleReadStatus(notification.id, notification.is_read)}
      />
    </Tooltip>,
    <Popconfirm
      title="Supprimer cette notification ?"
      onConfirm={() => deleteNotification(notification.id)}
      key="delete"
    >
      <Tooltip title="Supprimer">
        <Button type="text" danger icon={<DeleteOutlined />} />
      </Tooltip>
    </Popconfirm>
  ];

  // Rendu de la liste des notifications
  const renderNotificationsList = () => (
    <List
      loading={loading}
      dataSource={notifications}
      renderItem={(notification) => {
        const actionUrl = notification.actionUrl || notification.action_url;
        return (
          <List.Item 
            actions={getNotificationActions(notification)}
            style={{ 
              backgroundColor: notification.is_read ? 'transparent' : '#f6ffed',
              border: notification.is_read ? '1px solid #f0f0f0' : '1px solid #b7eb8f',
              cursor: actionUrl ? 'pointer' : 'default'
            }}
            onClick={() => actionUrl && handleNotificationClick(notification)}
          >
            <List.Item.Meta
              avatar={
                <Badge dot={!notification.is_read}>
                  <Avatar icon={getTypeIcon(notification.type)} />
                </Badge>
              }
              title={
                <Space>
                  <Text strong={!notification.is_read}>{notification.title}</Text>
                  <Tag color={getPriorityColor(notification.priority)}>
                    {notification.priority}
                  </Tag>
                </Space>
              }
              description={
                <Space direction="vertical" size="small">
                  <Text>{notification.message}</Text>
                  <Text type="secondary">
                    {dayjs(notification.created_at).fromNow()}
                  </Text>
                </Space>
              }
            />
          </List.Item>
        );
      }}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: (page, pageSize) => loadNotifications(page, pageSize),
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} notifications`
      }}
    />
  );

  // Rendu des statistiques d'envoi
  const renderSendingStats = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Total envoyé"
            value={sendingStats.total_sent}
            prefix={<SendOutlined />}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Envoyé aujourd'hui"
            value={sendingStats.today_sent}
            prefix={<MailOutlined />}
            valueStyle={{ color: '#3f8600' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Destinataires"
            value={sendingStats.total_recipients}
            prefix={<TeamOutlined />}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Taux de livraison"
            value={sendingStats.delivery_rate}
            suffix="%"
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: sendingStats.delivery_rate > 90 ? '#3f8600' : '#cf1322' }}
          />
        </Card>
      </Col>
    </Row>
  );

  // Rendu des statistiques d'évaluations
  const renderRatingsStats = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Total d'évaluations"
            value={filteredRatingsStats.total}
            prefix={<StarOutlined />}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Moyenne générale"
            value={filteredRatingsStats.average.toFixed(2)}
            prefix={<StarOutlined />}
            valueStyle={{ color: '#faad14' }}
            suffix="/ 5"
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Répartition des notes</Text>
          </div>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {[5, 4, 3, 2, 1].map((star) => {
              const count = filteredRatingsStats.breakdown[star as keyof typeof filteredRatingsStats.breakdown] || 0;
              const percentage = filteredRatingsStats.total > 0 ? (count / filteredRatingsStats.total * 100) : 0;
              return (
                <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text style={{ width: 30 }}>{star} ★</Text>
                  <div style={{ 
                    flex: 1, 
                    height: 8, 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: 4,
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${percentage}%`,
                      height: '100%',
                      backgroundColor: '#faad14',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                  <Text type="secondary" style={{ width: 30 }}>
                    {count}
                  </Text>
                </div>
              );
            })}
          </Space>
        </Card>
      </Col>
    </Row>
  );

  // Rendu de la liste des évaluations
  const renderRatingsList = () => (
    <List
      loading={ratingsLoading}
      dataSource={filteredRatings}
      renderItem={(rating: any) => (
        <List.Item>
          <Card style={{ width: '100%' }}>
            <List.Item.Meta
              avatar={
                <Avatar style={{ backgroundColor: '#0a543d' }}>
                  {rating.customer_name?.charAt(0)?.toUpperCase() || 'C'}
                </Avatar>
              }
              title={
                <Space direction="vertical" size="small">
                  <Space style={{ flexWrap: 'wrap' }}>
                    <Text strong>{rating.customer_name || 'Client'}</Text>
                    <Text type="secondary">→</Text>
                    <Tag color="blue" icon={<UserOutlined />}>
                      Technicien: {rating.technician_name || 'N/A'}
                    </Tag>
                  </Space>
                  <div>
                    <Rate disabled defaultValue={rating.rating} style={{ fontSize: 16 }} />
                    <Text style={{ marginLeft: 8, color: '#faad14', fontWeight: 'bold' }}>
                      {rating.rating}/5
                    </Text>
                  </div>
                </Space>
              }
              description={
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong>Intervention: {rating.intervention_title || `#${rating.intervention_id}`}</Text>
                  {rating.review && (
                    <div style={{ 
                      padding: 12, 
                      backgroundColor: '#f5f5f5', 
                      borderRadius: 8,
                      borderLeft: '3px solid #0a543d'
                    }}>
                      <Text italic>"{rating.review}"</Text>
                    </div>
                  )}
                  <Text type="secondary">
                    {dayjs(rating.rated_at).format('DD/MM/YYYY à HH:mm')}
                  </Text>
                </Space>
              }
            />
          </Card>
        </List.Item>
      )}
    />
  );

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <BellOutlined style={{ marginRight: 8 }} />
            Notifications
            {unreadCount > 0 && (
              <Badge count={unreadCount} style={{ marginLeft: 8 }} />
            )}
          </Title>
        </Col>
        <Col>
          <Space>
            <Button 
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
            >
              Tout marquer comme lu
            </Button>
            {!isAgent && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setSendModalVisible(true)}
              >
                Nouvelle notification
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Notifications reçues" key="notifications">
          <Card>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={8} lg={6}>
                <Select
                  placeholder="Filtrer par statut"
                  style={{ width: '100%' }}
                  allowClear
                  value={filters.is_read}
                  onChange={(value) => setFilters(prev => ({ ...prev, is_read: value }))}
                >
                  <Option value={false}>Non lues</Option>
                  <Option value={true}>Lues</Option>
                </Select>
              </Col>
              <Col xs={24} sm={8} lg={6}>
                <Select
                  placeholder="Filtrer par type"
                  style={{ width: '100%' }}
                  allowClear
                  value={filters.type}
                  onChange={(value: NotificationType | undefined) => setFilters(prev => ({ ...prev, type: value }))}
                >
                  <Option value="intervention_request">Demandes d'intervention</Option>
                  <Option value="intervention_assigned">Interventions assignées</Option>
                  <Option value="intervention_completed">Interventions terminées</Option>
                  <Option value="complaint_created">Nouvelles réclamations</Option>
                  <Option value="complaint_response">Réponses réclamations</Option>
                  <Option value="complaint_status_update">Statut réclamations</Option>
                  <Option value="order_created">Nouvelles commandes</Option>
                  <Option value="order_status_update">Statut commandes</Option>
                  <Option value="quote_created">Nouveaux devis</Option>
                  <Option value="payment_received">Paiements reçus</Option>
                  <Option value="general">Général</Option>
                </Select>
              </Col>
              <Col xs={24} sm={8} lg={6}>
                <Select
                  placeholder="Filtrer par priorité"
                  style={{ width: '100%' }}
                  allowClear
                  value={filters.priority}
                  onChange={(value: NotificationPriority | undefined) => setFilters(prev => ({ ...prev, priority: value }))}
                >
                  <Option value="urgent">Urgente</Option>
                  <Option value="high">Haute</Option>
                  <Option value="medium">Moyenne</Option>
                  <Option value="low">Basse</Option>
                </Select>
              </Col>
            </Row>

            {renderNotificationsList()}
          </Card>
        </TabPane>

        <TabPane tab="Évaluations reçues" key="ratings">
          <Card style={{ marginBottom: 16 }}>
            <Row align="middle" justify="space-between" gutter={[16, 16]}>
              <Col xs={24} sm={16} md={12} lg={8}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text strong style={{ color: '#0a543d' }}>
                    <UserOutlined style={{ marginRight: 6 }} />
                    Filtrer par technicien :
                  </Text>
                  <Select
                    style={{ width: '100%' }}
                    value={selectedTechnician}
                    onChange={(value) => setSelectedTechnician(value)}
                    placeholder="Sélectionner un technicien"
                    size="large"
                  >
                    <Option value="all">👥 Tous les techniciens ({ratings.length} évaluations)</Option>
                    {allTechniciansOptions.map((tech) => {
                      const count = ratings.filter(r => (r.technician_id ? String(r.technician_id) : r.technician_name) === tech.id || r.technician_name === tech.name).length;
                      return (
                        <Option key={tech.id} value={tech.id}>
                          👨‍🔧 {tech.name} ({count} avis)
                        </Option>
                      );
                    })}
                  </Select>
                </Space>
              </Col>
              {selectedTechnician !== 'all' && (
                <Col>
                  <Button 
                    onClick={() => setSelectedTechnician('all')}
                    type="default"
                    icon={<FilterOutlined />}
                  >
                    Réinitialiser le filtre
                  </Button>
                </Col>
              )}
            </Row>
          </Card>

          <Card>
            {renderRatingsStats()}
            {renderRatingsList()}
          </Card>
        </TabPane>
      </Tabs>

      {/* Modal de visualisation d'une notification */}
      <Modal
        title="Détails de la notification"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedNotification && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Type :</Text> {getTypeIcon(selectedNotification.type)} {selectedNotification.type}
              </Col>
              <Col span={12}>
                <Text strong>Priorité :</Text> 
                <Tag color={getPriorityColor(selectedNotification.priority)}>
                  {selectedNotification.priority}
                </Tag>
              </Col>
            </Row>
            <div>
              <Text strong>Titre :</Text>
              <Paragraph>{selectedNotification.title}</Paragraph>
            </div>
            <div>
              <Text strong>Message :</Text>
              <Paragraph>{selectedNotification.message}</Paragraph>
            </div>
            <div>
              <Text strong>Créé le :</Text>
              <Text>
                {['complaint_created','complaint_response','complaint_status_change','complaint_status_update'].includes(selectedNotification.type) && selectedNotification.data?.createdAt
                  ? dayjs(selectedNotification.data.createdAt).format('DD/MM/YYYY à HH:mm')
                  : dayjs(selectedNotification.created_at).format('DD/MM/YYYY à HH:mm')}
              </Text>
            </div>
            <div>
              <Text strong>Statut :</Text>
              <Tag color={selectedNotification.is_read ? 'green' : 'orange'}>
                {selectedNotification.is_read ? 'Lu' : 'Non lu'}
              </Tag>
            </div>
          </Space>
        )}
      </Modal>

      {/* Modal d'envoi de notification */}
      <Modal
        title="Envoyer une notification"
        open={sendModalVisible}
        onCancel={() => setSendModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={sendForm}
          layout="vertical"
          onFinish={sendNotification}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type"
                rules={[{ required: true, message: 'Veuillez sélectionner un type' }]}
              >
                <Select placeholder="Sélectionner un type">
                  <Option value="intervention_request">Demandes d'intervention</Option>
                  <Option value="complaint_created">Réclamations</Option>
                  <Option value="order_created">Commandes</Option>
                  <Option value="quote_created">Devis</Option>
                  <Option value="payment_received">Paiements</Option>
                  <Option value="general">Général</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priorité"
                rules={[{ required: true, message: 'Veuillez sélectionner une priorité' }]}
              >
                <Select placeholder="Sélectionner une priorité">
                  <Option value="urgent">Urgente</Option>
                  <Option value="high">Haute</Option>
                  <Option value="medium">Moyenne</Option>
                  <Option value="low">Basse</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="title"
            label="Titre"
            rules={[{ required: true, message: 'Veuillez saisir un titre' }]}
          >
            <Input placeholder="Titre de la notification" />
          </Form.Item>

          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: 'Veuillez saisir un message' }]}
          >
            <TextArea rows={4} placeholder="Contenu de la notification" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="recipient_type"
                label="Type de destinataires"
                rules={[{ required: true, message: 'Veuillez sélectionner le type de destinataires' }]}
              >
                <Select placeholder="Sélectionner les destinataires">
                  <Option value="all">Tous les utilisateurs</Option>
                  <Option value="customers">Clients</Option>
                  <Option value="technicians">Techniciens</Option>
                  <Option value="admins">Administrateurs</Option>
                  <Option value="specific">Utilisateurs spécifiques</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="send_immediately"
                label="Envoi"
                valuePropName="checked"
              >
                <Switch checkedChildren="Immédiat" unCheckedChildren="Programmé" />
              </Form.Item>
            </Col>
          </Row>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setSendModalVisible(false)}>
              Annuler
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Envoyer
            </Button>
          </Space>
        </Form>
      </Modal>

      {/* Modal de création de modèle */}
      <Modal
        title="Créer un modèle de notification"
        open={templateModalVisible}
        onCancel={() => setTemplateModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={templateForm}
          layout="vertical"
          onFinish={createTemplate}
        >
          <Form.Item
            name="name"
            label="Nom du modèle"
            rules={[{ required: true, message: 'Veuillez saisir un nom' }]}
          >
            <Input placeholder="Nom du modèle" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Veuillez sélectionner un type' }]}
          >
            <Select placeholder="Sélectionner un type">
              <Option value="intervention_request">Demandes d'intervention</Option>
              <Option value="complaint_created">Réclamations</Option>
              <Option value="order_created">Commandes</Option>
              <Option value="quote_created">Devis</Option>
              <Option value="payment_received">Paiements</Option>
              <Option value="general">Général</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="subject"
            label="Sujet"
            rules={[{ required: true, message: 'Veuillez saisir un sujet' }]}
          >
            <Input placeholder="Sujet du modèle" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Contenu"
            rules={[{ required: true, message: 'Veuillez saisir le contenu' }]}
          >
            <TextArea rows={6} placeholder="Contenu du modèle (utilisez {{variable}} pour les variables)" />
          </Form.Item>

          <Form.Item
            name="variables"
            label="Variables disponibles"
          >
            <Input placeholder="Séparez les variables par des virgules (ex: nom, email, date)" />
          </Form.Item>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setTemplateModalVisible(false)}>
              Annuler
            </Button>
            <Button type="primary" htmlType="submit">
              Créer le modèle
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default NotificationsPage;